import React, { useEffect, useState, useCallback } from 'react';
import { Link, useLocation, useMatch, useOutletContext } from "react-router-dom";
import moment from "moment";
// @ts-ignore
import permissions from 'invitemember-permissions-model';
import { fetchAPI } from "@/api";
import DetailsTable from "@/components/DetailsTable";
import PaymentsTable from "@/components/Tables/PaymentsTable";
import SubscriptionsTable from "@/components/Tables/SubscriptionsTable";
import RecurringPaymentsTable from "@/components/Tables/RecurringPaymentsTable";
import { getUserStatus } from '@/utils/utils';
import StaticLayoutInner from '@/layouts/StaticLayoutInner';
import ReloadCircle from '@/images/icons/ReloadCircle';

function User(props) {
  const location = useLocation();

  const [user, setUser] = useState(location.state);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { auth, project } = useOutletContext();
  let match = useMatch("/user/:userId");
  let userId = match.params.userId;

  const fetchData = useCallback(async controller => {
    try {
      setLoading(true);
      const response = await fetchAPI(
          "/project/user",
          auth.user.token,
          {
            projectId: project.projectId,
            userId: userId
          },
          { signal: controller.signal },
      );
      if (!response.ok) {
        throw new Error(`This is an HTTP error: The status is ${response.status}`);
      }
      let actualData = await response.json();
      setUser(actualData);
      setError(null);
    } catch (error) {
      let errorMessage = 'An unexpected error occurred';
      if (error.response?.message && error.name !== "AbortError") {
        errorMessage = error.response.message;
      }
      setError(errorMessage);
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  });

  useEffect(() => {
    const controller = new AbortController()

    fetchData(controller);

    return () => {
      controller.abort();
    };
  }, [match]);

  let fullName = user?.fullName;
  if (!user?.fullName) {
    fullName = user?.firstName
    if ( user?.lastName ) fullName += " " + user?.lastName;
  }

  let details = [];
  details.push({ label: "Registered", value: !user ? null : moment(user?.createdAt).format("MMM D, LT"), none: "Unknown" });
  details.push({ label: "Language", value: user?.languageCode, none: "—" });
  details.push({ label: "Email", value: user?.email, none: "—" });
  details.push({ label: "Telegram ID", value: user?.telegramUserId, none: "—" });
  if (user?.teamRoles) {
    details.push({
      label: "Team roles",
      value: permissions.getRolesFromUserPermissions(user?.teamRoles) ? permissions.getRolesFromUserPermissions(user?.teamRoles).name : "Custom",
      none: "—",
    });
  }

  if (user?.affiliateId) {
    details.push({ label: "Affiliate ID", value: user?.affiliateId, none: "—" });
  }
  // if (user?.isBanned) {
  // }

  if (user?.referredByUserId && user?.linkAffiliateId) {
    details.push({ label: "Referred by", value: <Link to={"/user/" + user?.referredByUserId} className="underline hover:no-underline">Affiliate #{user.linkAffiliateId}</Link>, none: "—" });
  }

  return (
    <StaticLayoutInner>
      <div className="bg-white border border-[#F2F2F2] rounded-xl py-4 md:py-4 px-4 md:px-5 flex-grow flex flex-col items-start">
        <h1 className="h2 mb-5">
          {(!fullName && !user?.email) && <div className={"animate-pulse bg-[#F2F2F2] rounded-full w-40 h-6 inline-block"}></div>}
          {(fullName) && <div>{fullName}</div>}
          {(!fullName && user?.email) && <div>{user?.email}</div>}
        </h1>
        <div className="flex gap-2">
          {!user || !project ? (
            <div className={"animate-pulse bg-[#F2F2F2] rounded-full w-32 h-3 mt-2 mb-1 inline-block"}></div>
          ) : getUserStatus({ user, project }, 'badges')}
        </div>
        <div className="mt-8 mb-1">
          <DetailsTable details={details} isLoading={isLoading} />
        </div>
      </div>

      <div className="mt-8">
        <h2 className="h3 mb-4">Subscriptions</h2>
        <SubscriptionsTable pageSize={5} filters={JSON.stringify({
          filters: [
            { key: "userId", condition: { eq: userId } }
          ]
        })} />
      </div>
      <div className="mt-8">
        <h2 className="h3 mb-4">Recurring payments</h2>
        <RecurringPaymentsTable pageSize={5} filters={JSON.stringify({
          filters: [
            { key: "userId", condition: { eq: userId } }
          ]
        })} />
      </div>
      <div className="mt-8">
        <h2 className="h3 mb-4">One-time payments</h2>
        <PaymentsTable pageSize={5} filters={JSON.stringify({
          filters: [
            { key: "userId", condition: { eq: userId } }
          ]
        })} />
      </div>
    </StaticLayoutInner>
  );
}

export default User;
